import React, { useState } from 'react';
import { X, Save, FolderOpen, Search, ClipboardCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { InspectionService } from '../../services/inspectionService';
import { GoogleDriveService } from '../../services/googleDrive';
import { DriveFolderSearchModal } from '../DriveIntegration';
import { Inspection, DriveFolder } from '../../types';

interface CreateInspectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInspectionCreated: (inspection: Inspection) => void;
}

export const CreateInspectionModal: React.FC<CreateInspectionModalProps> = ({
  isOpen,
  onClose,
  onInspectionCreated
}) => {
  const { firebaseUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [driveFolder, setDriveFolder] = useState<DriveFolder | null>(null);
  const [driveUrl, setDriveUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [validatingUrl, setValidatingUrl] = useState(false);
  const [error, setError] = useState('');
  const [isDriveSearchOpen, setIsDriveSearchOpen] = useState(false);

  const inspectionService = InspectionService.getInstance();
  const driveService = GoogleDriveService.getInstance();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!firebaseUser) {
      setError('User not authenticated');
      return;
    }

    if (!formData.name.trim()) {
      setError('Inspection name is required');
      return;
    }

    try {
      setSaving(true);
      setError('');

      console.log('🔄 Creating new inspection...');
      
      // Clean the drive folder data - only keep essential fields for Firestore
      const cleanDriveFolder = driveFolder ? {
        id: driveFolder.id,
        name: driveFolder.name,
        url: driveFolder.url
      } : undefined;

      const inspectionData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        status: 'draft' as const,
        createdBy: firebaseUser.uid,
        driveFolder: cleanDriveFolder
      };

      const inspectionId = await inspectionService.createInspection(inspectionData);
      
      console.log('✅ Inspection created with ID:', inspectionId);

      // Create the full inspection object to return
      const newInspection: Inspection = {
        id: inspectionId,
        ...inspectionData,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      onInspectionCreated(newInspection);
      onClose();
      
      // Reset form
      setFormData({ name: '', description: '' });
      setDriveFolder(null);
      setDriveUrl('');
    } catch (err: any) {
      console.error('❌ Error creating inspection:', err);
      setError(`Failed to create inspection: ${err.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDriveUrlValidation = async () => {
    if (!driveUrl.trim()) {
      setDriveFolder(null);
      return;
    }

    if (!firebaseUser) {
      setError('User not authenticated');
      return;
    }

    try {
      setValidatingUrl(true);
      setError('');

      console.log('🔍 Validating Drive URL...');
      
      const result = await driveService.validateFolderUrl(driveUrl.trim());
      
      if (result.valid && result.folder) {
        setDriveFolder(result.folder);
        console.log('✅ Valid Drive folder:', result.folder.name);
      } else {
        setError('Invalid Google Drive folder URL. Please check the URL and ensure you have access to the folder.');
        setDriveFolder(null);
      }
    } catch (err: any) {
      console.error('❌ Error validating Drive URL:', err);
      setError(`Failed to validate Drive URL: ${err.message || 'Unknown error'}`);
      setDriveFolder(null);
    } finally {
      setValidatingUrl(false);
    }
  };

  const handleDriveUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setDriveUrl(url);
    
    // Clear previous validation if URL is cleared
    if (!url.trim()) {
      setDriveFolder(null);
      setError('');
    }
  };

  const handleDriveUrlBlur = () => {
    if (driveUrl.trim() && driveUrl !== driveFolder?.url) {
      handleDriveUrlValidation();
    }
  };

  const handleRemoveDriveFolder = () => {
    setDriveFolder(null);
    setDriveUrl('');
  };

  const handleDriveFolderSelected = (folder: DriveFolder) => {
    setDriveFolder(folder);
    setDriveUrl(folder.url);
    setError('');
    console.log('✅ Drive folder selected:', folder.name);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <ClipboardCheck className="h-6 w-6 text-orange-600" />
            <h2 className="text-xl font-semibold text-gray-900">Create New Inspection</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Inspection Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Inspection Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Enter inspection name (e.g., Site Visit - 123 Main St)"
              required
            />
          </div>

          {/* Inspection Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Describe the inspection purpose and scope"
            />
          </div>

          {/* Google Drive Integration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Google Drive Folder (for saving report)
            </label>
            
            {!driveFolder ? (
              <div className="space-y-3">
                <div className="flex space-x-2">
                  <div className="flex-1">
                    <input
                      type="url"
                      value={driveUrl}
                      onChange={handleDriveUrlChange}
                      onBlur={handleDriveUrlBlur}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Paste Google Drive folder URL"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsDriveSearchOpen(true)}
                    className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    title="Search Drive folders"
                  >
                    <Search className="w-5 h-5" />
                  </button>
                </div>
                
                {validatingUrl && (
                  <div className="flex items-center text-sm text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Validating folder...
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-md">
                <div className="flex items-center space-x-2">
                  <FolderOpen className="w-5 h-5 text-orange-600" />
                  <div>
                    <div className="font-medium text-orange-900">{driveFolder.name}</div>
                    <div className="text-sm text-orange-700">Folder linked successfully</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveDriveFolder}
                  className="text-orange-600 hover:text-orange-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}
            
            <p className="text-xs text-gray-500 mt-2">
              The final inspection report will be saved as a Google Doc in this folder
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
            <h4 className="text-sm font-medium text-orange-900 mb-2">How it works:</h4>
            <ol className="text-sm text-orange-700 list-decimal list-inside space-y-1">
              <li>Upload or link a video walkthrough</li>
              <li>Pause at key points to capture screenshots</li>
              <li>Add numbered markers and notes to each screenshot</li>
              <li>Record audio commentary for each frame</li>
              <li>Generate an AI-powered inspection report</li>
            </ol>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || validatingUrl || !formData.name.trim()}
              className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Create Inspection
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Drive Folder Search Modal */}
      <DriveFolderSearchModal
        isOpen={isDriveSearchOpen}
        onClose={() => setIsDriveSearchOpen(false)}
        onFolderSelected={handleDriveFolderSelected}
      />
    </div>
  );
};
